/**
 * WhatIsFoodPulse — Social Pulse's on-network mirror of the flagship
 * https://www.foodpulse.net/what-is-food-pulse/ article.
 *
 * Purpose: when a visitor clicks "Food Pulse" in Social Pulse's nav
 * we want them to land somewhere warm, on-brand, with the full pitch
 * — and with clear top + bottom CTAs to visit foodpulse.net when
 * they're ready to actually use the platform.
 *
 * Content is a curated copy of the original article. Hero image is
 * the Unsplash photo from the source (attribution preserved).
 * Palette pulls from Food Pulse's official amber/moss accent so the
 * page still feels like a cousin of the parent site.
 */
import { Link } from "react-router-dom";
import {
  ExternalLink, ArrowRight, MapPin, ShoppingCart, Store, Package,
  Heart, Globe, Lock, Lightbulb, Sparkles, Utensils, Truck,
} from "lucide-react";
import UnsplashPhoto from "@/components/UnsplashPhoto";

// Food Pulse's own brand palette — kept in-component so it doesn't
// leak into Social Pulse's global design tokens. Bright, high-contrast
// version tuned for readability on the /food-pulse landing.
const FP = {
  bg:        "#FFFEF7",  // near-white cream for the page body
  ink:       "#1F1A11",  // near-black for main body text
  inkSoft:   "#3F361F",  // slightly warmer for secondary text
  amber:     "#B45309",  // primary accent
  amberDeep: "#7C2D12",  // darker amber for max-contrast text
  amberSoft: "#FDE7C3",  // pale for chips/cards
  moss:      "#4B5638",  // moss green
  mossSoft:  "#D9E1D0",
  cream:     "#FFF5DC",  // hero background (light, warm, always readable)
  gold:      "#F4C042",  // sunny yellow used only for a top accent bar
};

const HERO_IMG = "";

const VISIT_URL = "https://www.foodpulse.net";

export default function WhatIsFoodPulse() {
  return (
    <div style={{ background: FP.bg, color: FP.ink }} data-testid="what-is-food-pulse">
      {/* ═══ TOP visit-Food-Pulse strip ═══ */}
      <VisitStrip position="top" />

      {/* ═══ Hero — LIGHT background, big high-contrast type ═══ */}
      <section
        className="relative overflow-hidden border-b-4"
        style={{
          background: `linear-gradient(180deg, ${FP.cream} 0%, ${FP.amberSoft} 100%)`,
          borderColor: FP.amber,
        }}
      >
        <div className="max-w-4xl mx-auto px-4 lg:px-6 py-16 sm:py-20 relative">
          <span
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-mono uppercase tracking-widest rounded-full px-3 py-1.5 shadow-sm"
            style={{ background: "#fff", color: FP.amberDeep, border: `2px solid ${FP.amber}` }}
            data-testid="wifp-brand-chip"
          >
            <Utensils className="w-4 h-4" /> A Social Pulse network site · foodpulse.net
          </span>
          <h1
            className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
            style={{ color: FP.amberDeep }}
          >
            What is Food Pulse?
          </h1>
          <p
            className="mt-6 text-xl sm:text-2xl leading-snug max-w-[46ch] font-medium"
            style={{ color: FP.ink }}
          >
            Food Pulse creates a new category: a comprehensive, location-based
            food waste reduction network. Our mission is to help communities
            reduce waste, share resources, and make better use of surplus food.
          </p>
          <p className="mt-3 text-sm font-mono uppercase tracking-widest" style={{ color: FP.moss }}>
            June 27, 2025 · 5 min read
          </p>
        </div>
      </section>

      {/* Hero photo with credit — mirrors the source article */}
      <div className="max-w-4xl mx-auto px-4 lg:px-6 -mt-4" data-testid="wifp-hero-figure">
        <UnsplashPhoto
          src={HERO_IMG}
          alt="A vibrant farmers-market stall of fresh produce"
          credit={{
            photographer: { name: "Megan Thomas", username: "organicdesignco" },
          }}
          className="w-full h-64 sm:h-96 object-cover rounded-2xl shadow-lg ring-4 ring-white/60"
          captionClassName="mt-2 text-xs opacity-70"
          testid="wifp-hero-photo"
        />
      </div>

      {/* ═══ Body ═══ */}
      <article className="max-w-3xl mx-auto px-4 lg:px-6 py-12 space-y-10">
        {/* Welcome */}
        <Section icon={Globe} title="Welcome to Food Pulse — A Smarter Way to Reduce Food Waste">
          <p>
            <strong>Food Pulse</strong> is an innovative platform designed to bring together
            every player in the food supply chain — <strong>consumers, grocers,
            supermarkets, farmers, fishermen, wholesalers, restaurants, and food
            charities</strong> — to tackle food waste in real time.
          </p>
          <p>
            Unlike traditional solutions, Food Pulse creates a new category: <strong>a
            comprehensive database of all perishable food items with details,
            nutrition, and various recipes combined with a location-based food
            waste reduction network</strong>. Our mission is to help communities
            reduce waste, share resources, and make better use of surplus food.
          </p>
        </Section>

        {/* What makes it unique */}
        <Section icon={Sparkles} title="What Makes Food Pulse Unique?">
          <h3 className="font-display text-xl mt-2 mb-2" style={{ color: FP.amber }}>
            ✅ Real-Time, Location-Based Listings
          </h3>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>A comprehensive, intelligent database of perishable food items</strong>
              {" "}— fruits, vegetables, greens, meat, poultry, seafood, condiments,
              and prepared foods — featuring rich information on each item's
              characteristics, nutritional value, recipes, selection tips, storage
              methods, and cooking techniques. This empowers users to make informed
              choices, reduce waste, and enjoy healthier meals.
            </li>
            <li>Farmers and fishermen can publish their <strong>daily harvests and catches</strong> using GPS and WhatsApp integration.</li>
            <li>Vendors can list <strong>surplus or near-expiry products</strong> for sale or donation.</li>
            <li>Consumers can find <strong>hyperlocal deals</strong> or get notified about items on their wish lists.</li>
            <li>
              <strong>Food Pulse empowers both consumers and small vendors to
              collaboratively manage shopping and pantry/store inventories</strong>
              {" "}— enabling families, friends, and teams to shop smarter together.
              Track items with expiry dates, restock thresholds, and real-time
              alerts, each tagged with photos and precise storage locations on a map.
            </li>
            <li>
              <strong>Food Pulse enables small farmers, fishermen, and rural food
              stands to share their daily harvests and fresh catches in real
              time</strong>, complete with GPS tagging and availability updates —
              connecting them directly with local buyers and travelling consumers
              seeking fresh, local produce.
            </li>
          </ul>

          <h3 className="font-display text-xl mt-6 mb-2" style={{ color: FP.moss }}>
            ✅ Connected Food Ecosystem
          </h3>
          <p>Food Pulse connects:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Consumers</strong> to deals, wish lists, shopping, deliveries, and pantry management.</li>
            <li><strong>Vendors</strong> to nearby buyers, donors, and charities.</li>
            <li><strong>Charities</strong> to reliable sources of surplus food.</li>
            <li><strong>Wholesalers, grocers, farm stands</strong>, and even <strong>mobile food carts</strong> to one shared, intelligent platform.</li>
          </ul>
        </Section>

        {/* Consumer features */}
        <Section icon={ShoppingCart} title="Consumer Features">
          <h3 className="font-display text-lg mt-2 mb-2">👤 Personal Dashboard</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Add your profile, location, household details, and social links.</li>
            <li>Save preferred vendors (grocery stores, supermarkets, farm stands).</li>
          </ul>
          <h3 className="font-display text-lg mt-4 mb-2">💡 Smart Food Lists</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Wish List</strong> — items you want to buy, with price alerts and vendor links.</li>
            <li><strong>Shopping List</strong> — ready-to-purchase items with delivery or pickup options.</li>
            <li><strong>Pantry List</strong> — inventory tracking with photos, expiry dates, and restock alerts.</li>
            <li><strong>Expiry List</strong> — timely reminders for items nearing expiration.</li>
            <li><strong>Donation List</strong> — easily donate surplus food to local charities or individuals.</li>
          </ul>
          <h3 className="font-display text-lg mt-4 mb-2">🔔 Deals & Discounts</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>View real-time deals posted by vendors or neighbors.</li>
            <li>Sort by category, location, expiry, or price.</li>
          </ul>
        </Section>

        {/* Vendor features */}
        <Section icon={Store} title="Vendor Features">
          <p>
            Vendors can create business profiles and list products with full
            visibility across the platform.
          </p>
          <h3 className="font-display text-lg mt-4 mb-2">Vendor Types</h3>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {[
              "Supermarkets / Warehouse Stores",
              "Grocery Stores / Butcher Shops / Fish Stalls",
              "Restaurants / Cafes / Food Trucks",
              "Wholesalers / Distributors",
              "Farms / Farm Stands / Mobile Carts",
              "Fishermen / Fish Markets",
              "Food Pantries / Charities",
              "Delivery & Shopping Services",
            ].map((v) => (
              <div key={v} className="flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: FP.amber }} />
                <span>{v}</span>
              </div>
            ))}
          </div>
          <h3 className="font-display text-lg mt-4 mb-2">Vendor Details Captured</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li>Country, State, City · Name, Address, Phone, Email</li>
            <li>Website, Corporate Profile · Branches / Locations</li>
            <li>Management contact · Product inventory, deals, and surplus items</li>
          </ul>
        </Section>

        {/* Product catalogs */}
        <Section icon={Package} title="Product Catalogs">
          <h3 className="font-display text-lg mt-2 mb-2">🍎 Perishables</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Fruits, Vegetables, Herbs, Meat, Poultry, Seafood, Dairy, Bakery</li>
            <li>Includes: Name, Description, Photos, Nutrition Info, Notes</li>
          </ul>
          <h3 className="font-display text-lg mt-4 mb-2">🥫 Packaged & Processed Foods</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Cereals, Grains, Flour, Milk, Cheese, Pizza, Ice Cream, Chips, Candy, Chocolate, Sauces, Spices, Mixes</li>
            <li>Includes: Name, Photos, UPC Code, Category, Notes</li>
          </ul>
          <h3 className="font-display text-lg mt-4 mb-2">🍽️ Recipes</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Users and vendors can upload recipes with photos, ingredients, categories (veg/non-veg), and notes.</li>
            <li>Recipes link directly to items in your pantry or wish list.</li>
          </ul>
        </Section>

        {/* Charities */}
        <Section icon={Heart} title="Charities & Food Banks">
          <p>Food Pulse empowers charities and food banks to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Receive food donations in real time.</li>
            <li>Connect with vendors, farmers, and individuals.</li>
            <li>Create profiles with contact info, location maps, and delivery instructions.</li>
          </ul>
        </Section>

        {/* Location system */}
        <Section icon={MapPin} title="Integrated Location System">
          <p>
            Every listing — product, person, or vendor — is connected via a
            <strong> geographic database</strong>:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Country → State → City → GPS-based pin</li>
            <li>Enables hyperlocal visibility and alerts</li>
            <li>Helps with fast pick-up and delivery coordination</li>
          </ul>
        </Section>

        {/* UX & community */}
        <Section icon={Lock} title="User Experience & Community Impact">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Simple onboarding</strong> with phone or email.</li>
            <li><strong>WhatsApp integration</strong> for fast communication.</li>
            <li><strong>Social sharing features</strong> to spread deals, donations, and updates.</li>
            <li><strong>Reducing food waste</strong> while building a <strong>circular economy of care</strong>.</li>
          </ul>
        </Section>

        {/* Summary */}
        <Section icon={Lightbulb} title="Summary">
          <p>
            <strong>Food Pulse</strong> isn't just a marketplace. It's a <strong>movement</strong>
            {" "}toward smarter food management, community-driven distribution, and a
            zero-waste future.
          </p>
          <p className="font-display text-xl mt-3" style={{ color: FP.amber }}>
            Whether you're a consumer, vendor, or charity — Food Pulse is your pulse on food.
          </p>
        </Section>

        {/* Big brand CTA */}
        <div
          className="rounded-2xl p-8 text-center shadow-md ring-4 ring-white/40"
          style={{ background: `linear-gradient(135deg, ${FP.amberSoft} 0%, ${FP.mossSoft} 100%)` }}
          data-testid="wifp-mid-cta"
        >
          <Truck className="w-8 h-8 mx-auto mb-2" style={{ color: FP.amber }} />
          <h3 className="font-display text-2xl mb-1">Ready to join the movement?</h3>
          <p className="text-sm opacity-80 max-w-[42ch] mx-auto mb-4">
            Read the latest field reports, join a community kitchen near you, or
            list your farm-fresh surplus on Food Pulse today.
          </p>
          <a
            href={VISIT_URL}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-md transition"
            style={{ background: FP.amber }}
            data-testid="wifp-mid-cta-btn"
          >
            Visit foodpulse.net <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Back-to-network hint */}
        <p className="text-center text-sm opacity-70">
          Or continue browsing{" "}
          <Link to="/" className="underline" style={{ color: FP.amber }} data-testid="wifp-back-social-pulse">
            Social Pulse
          </Link>
          {" "}for more community stories and causes.
        </p>
      </article>

      {/* ═══ BOTTOM visit-Food-Pulse strip ═══ */}
      <VisitStrip position="bottom" />
    </div>
  );
}

/**
 * VisitStrip — bright, high-contrast banner at the top AND bottom of
 * the page. Uses a sunny yellow / cream background with the dark
 * amber text of Food Pulse so the CTA reads clearly, no muddy
 * gradient-on-dark. Different accents for top/bottom to feel
 * distinct but related.
 */
function VisitStrip({ position }) {
  const isTop = position === "top";
  return (
    <div
      className="w-full border-b-4"
      style={{
        background: isTop
          ? `linear-gradient(90deg, ${FP.gold} 0%, #FDBA74 100%)`
          : `linear-gradient(90deg, #FDBA74 0%, ${FP.gold} 100%)`,
        borderColor: FP.amberDeep,
      }}
      data-testid={`wifp-visit-strip-${position}`}
    >
      <div
        className="max-w-6xl mx-auto px-4 lg:px-6 py-3.5 flex flex-wrap items-center justify-between gap-3"
        style={{ color: FP.amberDeep }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Utensils className="w-6 h-6 shrink-0" strokeWidth={2.5} />
          <span className="text-base sm:text-lg font-semibold tracking-tight">
            {isTop
              ? "Explore recipes, deals and community kitchens on the full Food Pulse platform"
              : "Ready to reduce food waste in your neighbourhood? Join Food Pulse today."}
          </span>
        </div>
        <a
          href={VISIT_URL}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-base font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap ring-2 ring-white/60"
          style={{ background: FP.amberDeep, color: "#FFFEF7" }}
          data-testid={`wifp-visit-strip-${position}-btn`}
        >
          Visit foodpulse.net <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
        </a>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section data-testid={`wifp-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
      <div className="flex items-center gap-3 mb-4 pb-2 border-b-2" style={{ borderColor: FP.amberSoft }}>
        <Icon className="w-6 h-6" style={{ color: FP.amber }} strokeWidth={2.5} />
        <h2 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: FP.amberDeep }}>
          {title}
        </h2>
      </div>
      <div className="space-y-4 text-lg sm:text-xl leading-relaxed" style={{ color: FP.ink }}>
        {children}
      </div>
    </section>
  );
}
